import { useMemo, useState } from "react";
import { useStore } from "../store/store";
import { useT } from "../i18n/useT";
import { computeKPIs, kpisToCsv, kpisToPrintableHtml } from "@mensajeando/reports";

export function Reports() {
  const t = useT();
  const campaigns = useStore((s) => s.campaigns);
  const events = useStore((s) => s.events);
  const orgs = useStore((s) => s.organizations);
  const clients = useStore((s) => s.clients);

  const [campaignId, setCampaignId] = useState(campaigns[0]?.id ?? "");
  const campaign = campaigns.find((c) => c.id === campaignId);
  const client = clients.find((c) => c.id === campaign?.clientId);
  const org = orgs.find((o) => o.id === campaign?.organizationId);

  const kpis = useMemo(() => {
    if (!campaign) return undefined;
    return computeKPIs(events, {
      campaignId: campaign.id,
      startedConversations: campaign.startedConversations,
      completedConversations: campaign.completedConversations,
    });
  }, [campaign, events]);

  function downloadCsv() {
    if (!kpis) return;
    const csv = kpisToCsv(kpis);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${campaign?.name ?? "report"}.csv`);
  }

  function openPrintable() {
    if (!kpis || !campaign || !client || !org) return;
    const html = kpisToPrintableHtml(kpis, {
      clientName: client.name,
      campaignName: campaign.name,
      generatedAt: new Date().toLocaleString(),
      disclosureVersion: "2026-01-15.v1",
      methodology: "Sample drawn from registered campaign sends; responses parsed via flow choice matcher with EN/ES aliases. Opt-outs enforced by the compliance policy engine and removed from denominator for outreach-effectiveness rates.",
    });
    const w = window.open();
    if (w) { w.document.write(html); w.document.close(); }
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.reports}</h1>
        <div className="flex items-center gap-2">
          <select className="input" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn-secondary" onClick={downloadCsv}>{t.reports.csv}</button>
          <button className="btn-primary" onClick={openPrintable}>{t.reports.pdf}</button>
        </div>
      </header>

      {kpis && (
        <>
          <section className="grid grid-cols-4 gap-4 mb-6">
            <Card label="Sent" value={kpis.totals.sent} />
            <Card label="Delivered" value={kpis.totals.delivered} />
            <Card label="Failed" value={kpis.totals.failed} />
            <Card label="Opt-outs" value={kpis.totals.optOuts} />
            <Card label="Delivery rate" value={pct(kpis.rates.deliveryRate)} />
            <Card label="Response rate" value={pct(kpis.rates.responseRate)} />
            <Card label="Opt-out rate" value={pct(kpis.rates.optOutRate)} />
            <Card label="Completion rate" value={pct(kpis.rates.completionRate)} />
          </section>

          <section className="card card-pad mb-6">
            <h2 className="font-semibold mb-3">Answer distribution</h2>
            {Object.entries(kpis.answerDistribution).map(([k, vals]) => (
              <div key={k} className="mb-3">
                <div className="text-xs uppercase text-slate-500 mb-1">{k}</div>
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(vals).map(([v, c]) => (
                      <tr key={v} className="border-t border-slate-100">
                        <td className="py-1">{v}</td>
                        <td className="py-1 text-right">{c}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {Object.keys(kpis.answerDistribution).length === 0 && <p className="text-sm text-slate-500">No structured responses yet.</p>}
          </section>

          <section className="card card-pad">
            <h2 className="font-semibold mb-2">{t.reports.methodology}</h2>
            <p className="text-sm text-slate-600">
              Methodology and consent provenance are embedded in the exported report. The PDF export uses the browser's print-to-PDF so it works in static hosting; once a server is added, swap to a server-rendered PDF.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

function Card({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
